import { ControlledAutocomplete } from 'Components';
import { ICriteriaStyle } from '..';
import { Avatar } from '@mui/material';
import { AccordionArrowPurple } from 'Images';
import { useContext, useEffect, useState } from 'react';
import { SuppliersContext } from 'Context/SuppliersContext';
import {
  findResourceIdFromOptions,
  getSupplierResource,
  longestResourceOption,
  transformCitiesToStringArray,
  transformResourcesToStringArray,
} from 'Utils/suppliersResources';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useAppSelector, useIsDesktop } from 'Hooks';
import { SUPPLIER_RESULT_FILTER } from 'Enum/suppliers';

export const Location = ({
  autoCompleteSx,
  autoCompletePaperSx,
}: ICriteriaStyle) => {
  const { resources, filters, setFilters, dispatchSuppliers } =
    useContext(SuppliersContext);

  const defaultValue =
    Dictionary[DICTIONARY.Suppliers].resultPage.therapy.location;

  const [option, setOption] = useState<string>(defaultValue);

  const cities = useAppSelector(
    ({ generalOptions: { generalOptions } }) => generalOptions
  )?.cities;

  useEffect(() => {
    const region = getSupplierResource(resources.RGION, filters.region);
    if (region) setOption(region);

    const area = getSupplierResource(resources.BRNCD, filters.area);
    if (area) setOption(area);

    const city = cities?.find((city) => city.id === filters.city);
    if (city) setOption(city.value);
  }, [resources, filters.region, filters.city, filters.area, cities]);

  const isDesktop = useIsDesktop();

  const getOptionValue = (value: string) => {
    const region = findResourceIdFromOptions(resources.RGION, value);
    if (region) {
      const regionName = getSupplierResource(resources.RGION, region);
      setOption(regionName);
      return setFilters({ ...filters, region, city: null, area: null });
    }

    const area = findResourceIdFromOptions(resources.BRNCD, value);
    if (area) {
      const areaName = getSupplierResource(resources.BRNCD, area);
      setOption(areaName);
      return setFilters({ ...filters, area, region: null, city: null });
    }

    const city = cities?.find((city) => city.value === value);
    if (city) {
      const cityName = city.value;
      setOption(cityName);
      const updatedFilter = {
        ...filters,
        city: city.id,
        region: null,
        area: null,
      };
      setFilters(updatedFilter);
      dispatchSuppliers({
        type: SUPPLIER_RESULT_FILTER.Therapy,
        payload: { value: updatedFilter },
      });
      return;
    }
  };

  const resetLocation = () => {
    const updatedFilter = {
      ...filters,
      city: null,
      region: null,
      area: null,
    };
    setFilters(updatedFilter);
    dispatchSuppliers({
      type: SUPPLIER_RESULT_FILTER.Therapy,
      payload: { value: updatedFilter },
    });
  };

  const options: string[] = [
    defaultValue,
    ...transformResourcesToStringArray(resources.RGION),
    ...transformResourcesToStringArray(resources.BRNCD),
    ...transformCitiesToStringArray(cities),
  ];

  return (
    <ControlledAutocomplete
      label=''
      options={options}
      value={option}
      setValue={(value: string) => {
        if (value === defaultValue) {
          setOption(value);
          resetLocation();
        } else getOptionValue(value);
      }}
      style={{
        ...autoCompleteSx,
        '&&& .MuiOutlinedInput-input': {
          p: 0,
          width: `${option?.length}ch`,
        },
      }}
      paperSx={{
        ...autoCompletePaperSx,
        width: `${longestResourceOption(options) + 2}ch`,
      }}
      noResultsText={Dictionary[DICTIONARY.Suppliers].resultPage.noResults}
      endAdornment={
        isDesktop ? (
          <Avatar
            alt=''
            src={AccordionArrowPurple}
            sx={{ width: '30px', height: '30px' }}
          />
        ) : null
      }
      disableClearIcon
    />
  );
};
